import { IPromise, IQService, ITimeoutService } from "angular";
import { IModalScope } from "angular-ui-bootstrap";
import { StateService } from "@uirouter/angularjs";
import { AnalyticsService, Flavor, recommendation } from "@gtmhub/core/analytics";
import { IStateInit } from "@gtmhub/core/routing";
import { UIErrorHandlingService } from "@gtmhub/error-handling";
import { FeatureToggleService } from "@gtmhub/featureToggle";
import { FeatureFlag } from "@gtmhub/featureToggle/models";
import { ICreateKrKpiFilter, IInsight, IInsightSelectorData } from "@gtmhub/insights";
import { isElementInKPITableView, ungroupedGroupId } from "@gtmhub/kpis";
import { ICollection, IGtmhubRootScopeService } from "@gtmhub/models";
import { IAccess, UserAction } from "@gtmhub/sessions";
import { IUnitFormattingForm } from "@gtmhub/shared/controllers/unit-formatting";
import { AutoFieldsService } from "@gtmhub/shared/services/auto-fields";
import { PermissionsService, getCurrentUserId } from "@gtmhub/users";
import { CurrentUserService } from "@gtmhub/users/currentUserService";
import { toKeyMap } from "@gtmhub/util";
import { IGetKpisQueryParams, IKpi, IKpiFormData, IKpiInsightLink, IModifiableKpi } from "../models/models";
import { KpiService } from "../services/kpiService";
import { IItemModel, IUnitFormatting } from "./../../shared/models";

export interface IKpiInsightSelector {
  data: IInsightSelectorData;
  readOnly: boolean;
}

export interface IBuildInUnit {
  [unit: string]: { suffix: boolean };
}

export interface IModifiedKpiGroupIds {
  groupIdsToBeAdded: string[];
  deletedGroupIdsWithOrder: string[];
  newGroupNames: string[];
}

export interface IKpiFormScope extends IModalScope {
  isSharedKpiEnabled: boolean;
  isManagePermissionsEnabled: boolean;
  selectMultipleOwners(ids: string[]): void;
  indicators: {
    creating: boolean;
    patching: boolean;
    loading: boolean;
  };
  inheritsPermissions: boolean;
  buildInUnits: IBuildInUnit;
  isCreatingFromInsight: boolean;
  kpiId?: string;
  selectedKpiCellGroupId?: string;
  kpi: IModifiableKpi;
  accessActionsSet: UserAction[];
  disableSaveBtn: boolean;
  // contains fields that cannot be mapped directly to the KPI
  model: IItemModel;
  insightSelector: IKpiInsightSelector;
  canEdit(): boolean;
  builtInUnitChanged(): void;
  openFormattingOptions(): void;
  goToKpis(): void;
  hasManageDataPermission(): boolean;
  save(options: { valid: boolean }): void;
  selectGroups(ids: string[]): void;
  onInsightSelectorChange(): void;
  searchInsightSuggestions(name: string): void;
  changeInheritsPermissions(inheritsPermissions: boolean): void;
  getDefaultGranularPermissions(): IAccess;
  getOriginalKpi(): IKpi;
  enableSaveBtnOnUpdate(): void;
}

export class KpiFormCtrl implements IStateInit {
  private originalKpi: IKpi;
  private originalAccess: IAccess;
  private defaultGranularPermissions: IAccess;
  private isNumberFormattingEnabled: boolean;
  private isReorderKpiEnabled = false;
  private isManagePermissionsEnabled = false;

  public static $inject = [
    "$q",
    "$scope",
    "$rootScope",
    "$state",
    "formData",
    "KpiService",
    "UIErrorHandlingService",
    "UnitFormattingForm",
    "FeatureToggleService",
    "AnalyticsService",
    "CurrentUserService",
    "$timeout",
    "AutoFieldsService",
    "PermissionsService",
  ];

  constructor(
    private $q: IQService,
    private $scope: IKpiFormScope,
    private $rootScope: IGtmhubRootScopeService,
    private $state: StateService,
    private formData: IKpiFormData,
    private kpiService: KpiService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private unitFormattingForm: IUnitFormattingForm,
    private featureToggleService: FeatureToggleService,
    private analyticsService: AnalyticsService,
    private currentUserService: CurrentUserService,
    private $timeout: ITimeoutService,
    private autoFieldsService: AutoFieldsService,
    private permissionService: PermissionsService
  ) {}

  public stateInit(): IPromise<unknown> {
    const numberFormattingPromise = this.featureToggleService.isFeatureAvailable(FeatureFlag.NumberFormatting);
    const reorderKpiPromise = this.featureToggleService.isFeatureAvailable(FeatureFlag.ReorderKpis);
    const managePermissionsPromise = this.featureToggleService.isFeatureAvailable(FeatureFlag.ManageKPIsPermissions);

    return this.$q
      .all([numberFormattingPromise, reorderKpiPromise, managePermissionsPromise])
      .then(([isNumberFormattingEnabled, isReorderKpiEnabled, isManagePermissionsEnabled]) => {
        this.isNumberFormattingEnabled = isNumberFormattingEnabled;
        this.isReorderKpiEnabled = isReorderKpiEnabled;
        this.isManagePermissionsEnabled = isManagePermissionsEnabled;

        return this.init();
      });
  }

  private init = (): IPromise<unknown> => {
    if (!this.hasManageDataPermission()) {
      return;
    }

    this.$scope.selectMultipleOwners = this.selectMultipleOwners;
    this.$scope.builtInUnitChanged = this.builtInUnitChanged;
    this.$scope.openFormattingOptions = this.openFormattingOptions;
    this.$scope.goToKpis = this.goToKpis;
    this.$scope.canEdit = this.canEdit;
    this.$scope.hasManageDataPermission = this.hasManageDataPermission;
    this.$scope.save = this.save;
    this.$scope.selectGroups = this.selectGroups;
    this.$scope.onInsightSelectorChange = this.onInsightSelectorChange;
    this.$scope.$on("kpiTableLoaded", () => this.scrollToAndHighlightCell());
    this.$scope.searchInsightSuggestions = this.searchInsightSuggestions;
    this.$scope.changeInheritsPermissions = this.changeInheritsPermissions;
    this.$scope.getOriginalKpi = this.getOriginalKpi;
    this.$scope.getDefaultGranularPermissions = this.getDefaultGranularPermissions;
    this.$scope.enableSaveBtnOnUpdate = this.enableSaveBtnOnUpdate;

    this.$scope.disableSaveBtn = true;
    this.$scope.isManagePermissionsEnabled = this.isManagePermissionsEnabled;
    this.$scope.isCreatingFromInsight = false;
    this.$scope.indicators = {
      creating: false,
      patching: false,
      loading: false,
    };

    this.$scope.accessActionsSet = ["read", "update", "delete", "modifyPermissions"];

    this.$scope.buildInUnits = {
      $: { suffix: true },
      "€": { suffix: true },
      "£": { suffix: true },
      "%": { suffix: true },
    };

    this.$scope.selectedKpiCellGroupId = this.formData.selectedKpiCellGroupId;

    if (this.formData.insightLink) {
      this.initFromInsight();
      this.initModelFromKpi();
    } else {
      if (this.formData.kpiId) {
        this.$scope.indicators.loading = true;
        return this.kpiService.getKpi(this.formData.kpiId).then(
          (kpi) => {
            this.setKpiToScope(kpi);

            if (this.$scope.isManagePermissionsEnabled) {
              this.getDefaultAccessOnEdit();
            }

            this.initModelFromKpi();
            this.$scope.insightSelector = this.generateDynamicWizardDataForExistingKpi(kpi);
            this.$scope.indicators.loading = false;
          },
          (error) => {
            this.handleIndicatorError(error, "loading", { inline: true });
          }
        );
      } else {
        this.initForManualCreate();
        this.initModelFromKpi();
      }
    }
  };

  private scrollToAndHighlightCell = () => {
    const selector = `.details-cell-kpi-${this.formData.kpiId}-${this.formData.selectedKpiCellGroupId}`;

    const htmlElement = document.querySelector<HTMLElement>(selector);
    if (htmlElement && !isElementInKPITableView(htmlElement)) {
      htmlElement.classList.add("highlight-cell");
      const bottomScroll = htmlElement.offsetTop - document.getElementById("kpi-table").clientHeight + htmlElement.clientHeight * 2;
      document.getElementById("kpi-table").scrollTo({ top: bottomScroll });

      this.$timeout(function () {
        htmlElement.classList.remove("highlight-cell");
      }, 3000);
    }
  };

  private searchInsightSuggestions = (name: string) => {
    this.$rootScope.$broadcast("searchInsightSuggestions", { name });
  };

  private initModelFromKpi = (): void => {
    const { formatting } = this.$scope.kpi;
    const unit = formatting ? (formatting.prefix === "" ? formatting.suffix : formatting.prefix) : "";
    this.$scope.kpi.groups = this.$scope.kpi.groups || [];
    this.$scope.model = {
      formatting: {
        unit,
        isCustom: unit !== "" && !Object.keys(this.$scope.buildInUnits).includes(unit),
      },
    };
  };

  private initFromInsight = (): void => {
    this.$scope.isCreatingFromInsight = true;

    const { insight, filters, fieldName, initialValue } = this.formData.insightLink;
    this.$scope.kpi = this.createDefaultPatchableKpi();
    this.$scope.kpi.name = insight.title;
    this.$scope.kpi.description = insight.description;
    this.$scope.kpi.formatting = this.createKpiFormattingFromInsight(insight);

    this.$scope.insightSelector = {
      data: {
        settings: filters,
        insightName: insight.name,
        fieldName,
        initialValue,
      },
      readOnly: false,
    };

    if (this.$scope.isManagePermissionsEnabled) {
      this.getDefaultAccessOnCreate();
    }
  };

  private selectMultipleOwners = (ids: string[]): void => {
    if (!ids.length) return;

    this.$scope.kpi.ownerIds = ids;
  };

  private createDefaultPatchableKpi = (): IModifiableKpi => {
    return {
      name: "",
      description: "",
      aggregation: "last",
      targetOperator: "should_increase",
      ownerIds: [getCurrentUserId()],
      formatting: this.createDefaultKpiFormatting(),
    };
  };

  private createDefaultKpiFormatting = (): IUnitFormatting => {
    if (this.isNumberFormattingEnabled) {
      return {
        fractionSize: -1,
        prefix: "",
        suffix: "",
      };
    } else {
      return {
        fractionSize: 0,
        prefix: "",
        suffix: "",
      };
    }
  };

  private createNewKpiFromInsight = (): IModifiableKpi => {
    const kpi = angular.copy(this.$scope.kpi) as IModifiableKpi;

    kpi.insight = {
      insightName: this.$scope.insightSelector.data.insightName,
      fieldName: this.$scope.insightSelector.data.fieldName,
      settings: this.$scope.insightSelector.data.settings,
      insightValue: this.$scope.insightSelector.data.initialValue,
    };

    return kpi;
  };

  private createKpiFormattingFromInsight = (insight: IInsight): IUnitFormatting => {
    const formatting = insight.formatting;
    if (!formatting) {
      return this.createDefaultKpiFormatting();
    }

    const unit = formatting.unit || "";
    if (this.isNumberFormattingEnabled) {
      return {
        prefix: formatting.suffix ? "" : unit,
        suffix: formatting.suffix ? unit : "",
        fractionSize: Number(insight.formatting.decimalSize),
      };
    } else {
      return {
        prefix: formatting.suffix ? "" : unit,
        suffix: formatting.suffix ? unit : "",
        fractionSize: Number(insight.formatting.decimalSize) || 0,
      };
    }
  };

  private onKpiCreated = (kpi: IKpi): void => {
    this.setKpiToScope(kpi);
    if (this.isReorderKpiEnabled) {
      this.handleMyKpisOrderInUserSettings(kpi);
    }

    const kpiClone = angular.copy(kpi);
    this.$rootScope.$broadcast("kpiCreated", kpiClone);
  };

  private setKpiToScope = (kpi: IKpi): void => {
    this.$scope.kpiId = kpi.id;
    this.$scope.kpi = {
      aggregation: kpi.aggregation,
      description: kpi.description,
      formatting: angular.copy(kpi.formatting),
      name: kpi.name,
      ownerIds: kpi.ownerIds,
      targetOperator: kpi.targetOperator,
      groups: kpi.groups,
      groupsOrder: kpi.groupsOrder,
    };

    if (this.isManagePermissionsEnabled) {
      this.$scope.kpi.access = kpi.access;
      this.originalAccess = angular.copy(kpi.access);
      this.$scope.inheritsPermissions = kpi.access?.inherits;
    }

    this.originalKpi = angular.copy(kpi);
  };

  private handleMyKpisOrderInUserSettings = (kpi: IModifiableKpi) => {
    const myKpisOrder: string[] = this.currentUserService.getUserSetting("myKpiOrder");
    if (myKpisOrder && !!kpi.ownerIds) {
      const kpiId = kpi.id || this.$scope.kpiId;

      if (!myKpisOrder.includes(kpiId) && kpi.ownerIds.includes(getCurrentUserId())) {
        myKpisOrder.push(kpiId);
        this.currentUserService.setUserSetting({ myKpiOrder: myKpisOrder });
      } else if (myKpisOrder.includes(kpiId)) {
        const newMyKpiOrder = myKpisOrder.splice(myKpisOrder.indexOf(kpiId), 1);
        this.currentUserService.setUserSetting({ myKpiOrder: newMyKpiOrder });
      }
    }
  };

  private generateDynamicWizardDataForExistingKpi = (kpi: IKpi): IKpiInsightSelector => {
    return {
      data: kpi.insightLink ? { ...kpi.insightLink } : ({} as IInsightSelectorData),
      readOnly: false,
    };
  };

  private canEdit = (): boolean => {
    return !!this.$scope.kpiId;
  };

  private initForManualCreate = (): void => {
    this.$scope.kpiId = null;
    this.$scope.kpi = this.createDefaultPatchableKpi();

    /** workaround for type errors in insight-selector when 'data' is null */
    this.$scope.insightSelector = {
      data: {} as IInsightSelectorData,
      readOnly: false,
    };

    // no need to make requests if the feature is disabled
    if (this.$scope.isManagePermissionsEnabled) {
      this.getDefaultAccessOnCreate();
    }
  };

  private getDefaultAccessOnCreate(): IPromise<void> {
    this.$scope.kpi.access = {
      inherits: true,
      permissions: null,
    };
    this.$scope.inheritsPermissions = this.$scope.kpi.access.inherits;
    return this.permissionService.getDefaultAccess("kpi").then(
      (access) => {
        this.defaultGranularPermissions = access;
        this.$scope.kpi.access = access;
      },
      (error) => this.uiErrorHandlingService.handleIndicator(error, { indicator: "loading", inline: true })
    );
  }

  private getDefaultAccessOnEdit(): IPromise<void> {
    return this.permissionService.getDefaultAccessById("kpi", this.$scope.kpiId).then(
      (access) => {
        this.defaultGranularPermissions = access;
      },
      (error) => this.uiErrorHandlingService.handleModal(error)
    );
  }

  private openFormattingOptions = (): void => {
    this.unitFormattingForm.open({ formatting: this.$scope.kpi.formatting, model: this.$scope.model });
  };

  private builtInUnitChanged = (): void => {
    const { unit } = this.$scope.model.formatting;
    const isSuffix = this.$scope.buildInUnits[unit]?.suffix;

    const { formatting } = this.$scope.kpi;
    formatting.prefix = !isSuffix ? unit : "";
    formatting.suffix = isSuffix ? unit : "";

    this.$scope.model.formatting.isCustom = false;
  };

  private goToKpis = (): void => {
    this.$rootScope.noNavigation = false;
    this.$scope.$close();
    this.$state.go("gtmhub.kpis");
  };

  private hasManageDataPermission = (): boolean => {
    if (this.isManagePermissionsEnabled) {
      return this.$rootScope.hasPermission("ManageKPIs");
    }

    return this.$rootScope.hasPermission("ManageData");
  };

  private handleIndicatorError = <E>(err: E, indicator: string, params: { inline: boolean }): void => {
    this.uiErrorHandlingService.handleIndicator(err, { indicator, inline: params.inline });
  };

  private save = ({ valid }: { valid: boolean }): void => {
    if (!valid) {
      return;
    }

    if (this.$scope.indicators.creating || this.$scope.indicators.patching) {
      return;
    }

    if (this.$scope.kpiId) {
      this.patchKpi();
    } else {
      this.isReorderKpiEnabled ? this.createKpiWithReorder() : this.createKpi();
    }

    if (this.$scope.insightSelector?.data?.insightTemplate) {
      const payload = this.autoFieldsService.getRecommendedInsightAnalyticsData(this.$scope.insightSelector.data);
      this.autoFieldsService.sendInsightRecommendtationFeedback(payload);
    }

    /** track kpis created with auto-insight-suggestions help only when creating a new kpi */
    if (!this.$scope.kpiId && this.$scope.insightSelector?.data) {
      this.analyticsService.track("KPI Insight Suggestion Created", {
        totalInsightsSuggested: this.$scope.insightSelector.data.suggestedInsights?.length || 0,
        ...(this.$scope.insightSelector.data.insightTemplate && {
          insightSuggestedId: this.$scope.insightSelector.data.insightTemplate.id,
        }),
        isAutomationUsed: !!this.$scope.insightSelector.data.insightTemplate,
      });
    }
  };

  private patchKpi = (): void => {
    const patchableKpi = this.createDiffPatchableKpi();

    if (!patchableKpi) {
      this.$scope.$close();
      return;
    }

    patchableKpi.id = this.$scope.kpiId;

    this.$scope.indicators.patching = true;

    if (patchableKpi.groups) {
      if (this.isReorderKpiEnabled) {
        this.handlePatchableKpisGroups(patchableKpi);
      } else {
        const existingGroupIdsToAdd = patchableKpi.groups.filter((group) => group.indexOf("new-") < 0);
        const newGroupNames = patchableKpi.groups.filter((group) => !existingGroupIdsToAdd.includes(group));

        if (newGroupNames.length > 0) {
          this.createNewGroupsAndProcessKpi([patchableKpi], newGroupNames, existingGroupIdsToAdd);
        } else {
          patchableKpi.groups = existingGroupIdsToAdd;
          this.patchKpiCall([patchableKpi]);
        }
      }
    } else {
      this.patchKpiCall([patchableKpi]);
    }
  };

  private buildModifiedKpiGroupIds = (patchableKpi: IModifiableKpi): IModifiedKpiGroupIds => {
    const modifiedKpiGroupIds: IModifiedKpiGroupIds = {
      groupIdsToBeAdded: [],
      newGroupNames: [],
      deletedGroupIdsWithOrder: [],
    };

    modifiedKpiGroupIds.groupIdsToBeAdded = patchableKpi.groups?.filter((group) => group.indexOf("new-") < 0) || [];
    modifiedKpiGroupIds.newGroupNames = patchableKpi.groups?.filter((group) => !modifiedKpiGroupIds.groupIdsToBeAdded.includes(group)) || [];
    if (this.originalKpi.groupsOrder) {
      modifiedKpiGroupIds.deletedGroupIdsWithOrder = Object.keys(this.originalKpi.groupsOrder).filter((orderedGroupId) => !patchableKpi.groups.includes(orderedGroupId));
    }

    return modifiedKpiGroupIds;
  };

  private handlePatchableKpisGroups = (patchableKpi: IModifiableKpi) => {
    const modifiedKpiGroupIds: IModifiedKpiGroupIds = this.buildModifiedKpiGroupIds(patchableKpi);

    if (this.originalKpi.groupsOrder) {
      patchableKpi.groupsOrder = this.originalKpi.groupsOrder;
    }

    if (modifiedKpiGroupIds.groupIdsToBeAdded.length === 0 && modifiedKpiGroupIds.deletedGroupIdsWithOrder.length === 0 && modifiedKpiGroupIds.newGroupNames.length > 0) {
      this.createNewGroupsAndProcessKpi([patchableKpi], modifiedKpiGroupIds.newGroupNames, modifiedKpiGroupIds.groupIdsToBeAdded);
      return;
    }

    //ungrouped should be handled first as it is a special case since we dont have 'ungrouped' group in the database
    if (modifiedKpiGroupIds.deletedGroupIdsWithOrder.includes(ungroupedGroupId)) {
      this.moveKpiFromOrderedUngrouped(modifiedKpiGroupIds, patchableKpi);
    } else if (patchableKpi.groups.length === 0) {
      this.removeAllGroupsAndMoveKpiToUngrouped(modifiedKpiGroupIds, patchableKpi);
    } else {
      this.orderAndPatchKpis(modifiedKpiGroupIds, patchableKpi, [patchableKpi]);
    }
  };

  private moveKpiFromOrderedUngrouped = (modifiedKpiGroupIds: IModifiedKpiGroupIds, patchableKpi: IModifiableKpi) => {
    const kpisToPatch: IModifiableKpi[] = [patchableKpi];

    this.kpiService
      .getKpis({ ungroupped: true })
      .then((kpis) => {
        const orderedKpisToPatch = kpis.items
          .filter((kpi) => kpi.id !== patchableKpi.id)
          .sort((a, b) => a.groupsOrder[ungroupedGroupId] - b.groupsOrder[ungroupedGroupId]);

        kpisToPatch.push(...this.reorderGroup(orderedKpisToPatch, ungroupedGroupId));
        modifiedKpiGroupIds.deletedGroupIdsWithOrder.splice(modifiedKpiGroupIds.deletedGroupIdsWithOrder.indexOf(ungroupedGroupId), 1);

        delete patchableKpi.groupsOrder[ungroupedGroupId];
      })
      .then(() => {
        this.orderAndPatchKpis(modifiedKpiGroupIds, patchableKpi, kpisToPatch);
      });
  };

  private removeAllGroupsAndMoveKpiToUngrouped = (modifiedKpiGroupIds: IModifiedKpiGroupIds, patchableKpi: IModifiableKpi) => {
    const kpisToPatch: IModifiableKpi[] = [patchableKpi];

    this.kpiService.getKpis({ ungroupped: true }).then((kpis) => {
      const ungroupedKpis = kpis.items.filter((kpi) => kpi.groupsOrder && kpi.groupsOrder[ungroupedGroupId] !== undefined);
      if (ungroupedKpis.length > 0) {
        patchableKpi.groupsOrder = patchableKpi.groupsOrder || {};
        patchableKpi.groupsOrder[ungroupedGroupId] = ungroupedKpis.length;
      }

      if (modifiedKpiGroupIds.deletedGroupIdsWithOrder.length === 0) {
        patchableKpi.groups = modifiedKpiGroupIds.groupIdsToBeAdded;
        this.patchKpiCall(kpisToPatch);
        return;
      }

      this.kpiService
        .getKpis({ groups: modifiedKpiGroupIds.deletedGroupIdsWithOrder.join(","), ungroupped: false })
        .then((kpis) => {
          this.reorderKpisInDeletedGroups(kpis, modifiedKpiGroupIds.deletedGroupIdsWithOrder, patchableKpi, kpisToPatch);
        })
        .then(() => {
          patchableKpi.groups = modifiedKpiGroupIds.groupIdsToBeAdded;
          this.patchKpiCall(kpisToPatch);
        });
    });
  };

  private orderAndPatchKpis = (modifiedKpiGroupIds: IModifiedKpiGroupIds, patchableKpi: IModifiableKpi, kpisToPatch: IModifiableKpi[]) => {
    this.kpiService
      .getKpis({ groups: [...modifiedKpiGroupIds.groupIdsToBeAdded, ...modifiedKpiGroupIds.deletedGroupIdsWithOrder].join(","), ungroupped: false })
      .then((kpis) => {
        this.reorderKpisInDeletedGroups(kpis, modifiedKpiGroupIds.deletedGroupIdsWithOrder, patchableKpi, kpisToPatch);
        this.addKpiToOrderedGroups(kpis, modifiedKpiGroupIds.groupIdsToBeAdded, patchableKpi);
      })
      .then(() => {
        if (modifiedKpiGroupIds.newGroupNames.length > 0) {
          this.createNewGroupsAndProcessKpi(kpisToPatch, modifiedKpiGroupIds.newGroupNames, modifiedKpiGroupIds.groupIdsToBeAdded);
        } else {
          patchableKpi.groups = modifiedKpiGroupIds.groupIdsToBeAdded;
          this.patchKpiCall(kpisToPatch);
        }
      });
  };

  private reorderKpisInDeletedGroups = (kpis: ICollection<IKpi>, deletedGroupIdsWithOrder: string[], patchableKpi: IModifiableKpi, kpisToPatch) => {
    if (deletedGroupIdsWithOrder.length > 0) {
      deletedGroupIdsWithOrder.forEach((deletedGroupIdWithOrder) => {
        const kpisInDeletedGroupWithOrder = kpis.items
          .filter((kpi) => kpi.groups.includes(deletedGroupIdWithOrder) && kpi.id !== this.$scope.kpiId)
          .sort((a, b) => a.groupsOrder[deletedGroupIdWithOrder] - b.groupsOrder[deletedGroupIdWithOrder]);

        kpisToPatch.push(...this.reorderGroup(kpisInDeletedGroupWithOrder, deletedGroupIdWithOrder));
        delete patchableKpi.groupsOrder[deletedGroupIdWithOrder];
      });
    }
  };

  private addKpiToOrderedGroups = (kpis: ICollection<IKpi>, groupIdsToBeAdded: string[], patchableKpi: IModifiableKpi) => {
    if (groupIdsToBeAdded.length > 0) {
      groupIdsToBeAdded.forEach((existingGroupId) => {
        const numberOfOrderedKpisInGroup = kpis.items.filter(
          (kpi) => kpi.id !== patchableKpi.id && kpi.groupsOrder && kpi.groupsOrder[existingGroupId] !== undefined
        ).length;

        if (numberOfOrderedKpisInGroup > 0) {
          patchableKpi.groupsOrder = patchableKpi.groupsOrder || {};
          patchableKpi.groupsOrder[existingGroupId] = numberOfOrderedKpisInGroup;
        }
      });
    }
  };

  private reorderGroup = (kpisInGroup: IKpi[], groupId: string): IModifiableKpi[] => {
    const kpisToPatch: IModifiableKpi[] = [];
    for (let i = 0; i < kpisInGroup.length; i++) {
      const kpiFromGroupToPatch: IModifiableKpi = {};
      kpiFromGroupToPatch.id = kpisInGroup[i].id;
      kpiFromGroupToPatch.groupsOrder = kpisInGroup[i].groupsOrder || {};
      kpiFromGroupToPatch.groupsOrder[groupId] = i;
      kpisToPatch.push(kpiFromGroupToPatch);
    }

    return kpisToPatch;
  };

  private applyPatchToOriginalKpi = (patchableKpi: IModifiableKpi): void => {
    for (const field of Object.keys(patchableKpi)) {
      if (field === "insight") {
        const { insightName, fieldName, settings } = { ...patchableKpi.insight };
        this.originalKpi.insightLink = insightName ? { insightName, fieldName, settings } : null;
      } else {
        this.originalKpi[field] = patchableKpi[field];
      }
    }
  };

  private getPatchableKpiInsight = (patchableKpi: IModifiableKpi): IModifiableKpi => {
    const originalKpiInsightLink: IKpiInsightLink = this.originalKpi.insightLink || <IKpiInsightLink>{};
    const insightSelectorData: IInsightSelectorData = this.$scope.insightSelector.data || <IInsightSelectorData>{};

    // this equality check will detect changes like [1, 2] => [2, 1] and will trigger a patch in this case
    const areFiltersChanged = (originalFilters: ICreateKrKpiFilter[], currentFilters: ICreateKrKpiFilter[]): boolean => {
      if (originalFilters.length !== currentFilters.length) {
        return true;
      }

      const toNameAndValueProps = (filter: ICreateKrKpiFilter): ICreateKrKpiFilter => ({ name: filter.name, value: filter.value });

      return !angular.equals(originalFilters.map(toNameAndValueProps), currentFilters.map(toNameAndValueProps));
    };

    if (
      originalKpiInsightLink.insightName !== insightSelectorData.insightName ||
      originalKpiInsightLink.fieldName !== insightSelectorData.fieldName ||
      areFiltersChanged(originalKpiInsightLink.settings || [], insightSelectorData.settings || [])
    ) {
      if (!patchableKpi) {
        patchableKpi = {};
      }

      const { insightName, fieldName, settings, initialValue } = { ...insightSelectorData };
      patchableKpi.insight = insightName ? { insightName, fieldName, settings, insightValue: initialValue } : null;
    }

    return patchableKpi;
  };

  private createDiffPatchableKpi = (): IModifiableKpi => {
    let patchableKpi: IModifiableKpi;

    // checking the 'non-dynamic' fields of the kpi - without insightLink
    for (const field of Object.keys(this.$scope.kpi)) {
      const originalValue = this.originalKpi[field];
      const currentValue = this.$scope.kpi[field];
      if (!angular.equals(originalValue, currentValue)) {
        if (!patchableKpi) {
          patchableKpi = {};
        }
        patchableKpi[field] = currentValue;
      }
    }

    return this.getPatchableKpiInsight(patchableKpi);
  };

  private createKpiWithReorder = (): void => {
    const newKpi =
      this.$scope.insightSelector && this.$scope.insightSelector.data && this.$scope.insightSelector.data.insightName
        ? this.createNewKpiFromInsight()
        : (this.$scope.kpi as IModifiableKpi);

    this.$scope.indicators.creating = true;

    const existingGroupIdsToAdd = newKpi.groups.filter((group) => group.indexOf("new-") < 0);
    const newGroupNames = newKpi.groups.filter((group) => !existingGroupIdsToAdd.includes(group));
    let query: IGetKpisQueryParams;

    if (existingGroupIdsToAdd.length > 0) {
      query = { groups: existingGroupIdsToAdd.join(","), ungroupped: false };
    } else if (newGroupNames.length === 0) {
      query = { ungroupped: true };
    }

    this.$q
      .resolve(this.kpiService.getKpis(query))
      .then((kpis) => {
        if (existingGroupIdsToAdd.length > 0) {
          existingGroupIdsToAdd.forEach((existingGroupId) => {
            const numberOfOrderedKpisInGroup = kpis.items.filter((kpi) => kpi.groupsOrder && kpi.groupsOrder[existingGroupId] !== undefined).length;
            if (numberOfOrderedKpisInGroup > 0) {
              newKpi.groupsOrder = newKpi.groupsOrder || {};
              newKpi.groupsOrder[existingGroupId] = numberOfOrderedKpisInGroup;
            }
          });
        } else if (newGroupNames.length === 0 && kpis.items.length > 0 && kpis.items[0].groupsOrder && kpis.items[0].groupsOrder["ungrouped"] !== undefined) {
          newKpi.groupsOrder = newKpi.groupsOrder || {};
          newKpi.groupsOrder["ungrouped"] = kpis.items.length;
        }
      })
      .then(() => {
        if (newGroupNames.length > 0) {
          this.createNewGroupsAndProcessKpi([newKpi], newGroupNames, existingGroupIdsToAdd);
        } else {
          if (existingGroupIdsToAdd.length > 0) {
            newKpi.groups = existingGroupIdsToAdd;
          }

          this.createKpiCall(newKpi);
        }
      });
  };

  private createKpi = (): void => {
    const newKpi =
      this.$scope.insightSelector && this.$scope.insightSelector.data && this.$scope.insightSelector.data.insightName
        ? this.createNewKpiFromInsight()
        : (this.$scope.kpi as IModifiableKpi);

    this.$scope.indicators.creating = true;

    const existingGroupIdsToAdd = newKpi.groups.filter((group) => group.indexOf("new-") < 0);
    const newGroupNames = newKpi.groups.filter((group) => !existingGroupIdsToAdd.includes(group));

    if (newGroupNames.length > 0) {
      this.createNewGroupsAndProcessKpi([newKpi], newGroupNames, existingGroupIdsToAdd);
    } else {
      newKpi.groups = existingGroupIdsToAdd;
      this.createKpiCall(newKpi);
    }
  };

  private createNewGroupsAndProcessKpi = (patchableKpis: IModifiableKpi[], newGroupNames: string[], existingGroupIdsToAdd: string[]) => {
    const promises = [];
    const kpi = patchableKpis[0];
    newGroupNames.forEach((name: string) => {
      const groupName = name.substring(name.indexOf("new-") + 4);
      const promise = this.kpiService.createKpiGroup({ name: groupName });
      promises.push(promise);
    });

    this.$q.all(promises).then((groupPromises) => {
      const groupIdsToAdd = existingGroupIdsToAdd;
      groupIdsToAdd.push(...groupPromises.map((groupPromise) => groupPromise.id));
      this.$rootScope.$broadcast("kpiGroupsCreated", groupPromises);
      kpi.groups = groupIdsToAdd;
      if (this.$scope.kpiId) {
        this.patchKpiCall(patchableKpis);
      } else {
        this.createKpiCall(kpi);
      }
    });
  };

  private createKpiCall = (newKpi: IModifiableKpi) => {
    this.kpiService.createKpi(newKpi).then(
      (kpi: IKpi) => {
        this.$scope.indicators.creating = false;
        this.onKpiCreated(kpi);
        this.trackRecommendationHelpfulness(kpi);
        if (!this.$scope.isCreatingFromInsight) {
          this.$scope.$close();
        }
      },
      (err) => this.handleIndicatorError(err, "creating", { inline: true })
    );
  };

  private trackRecommendationHelpfulness(kpi: IKpi): void {
    //recommendation is helpful if user creates a KPI and leaves current user as KPI owner
    this.analyticsService.track(recommendation, { is_useful: kpi.ownerIds.includes(getCurrentUserId()), flavor: Flavor.SMART_DEFAULT, action: "creates_kpi;owner" });
  }

  private patchKpiCall = (kpisToPatch: IModifiableKpi[]) => {
    if (this.isReorderKpiEnabled) {
      this.kpiService.patchKpis(toKeyMap("id", kpisToPatch)).then(
        () => {
          this.$scope.indicators.patching = false;

          this.applyPatchToOriginalKpi(kpisToPatch[0]);
          this.handleMyKpisOrderInUserSettings(kpisToPatch[0]);

          const kpiClone = angular.copy(this.originalKpi);
          kpisToPatch.shift();
          this.$rootScope.$broadcast("kpiUpdated", kpiClone, kpisToPatch);
          this.$scope.$close();
        },
        (err) => this.handleIndicatorError(err, "patching", { inline: true })
      );
    } else {
      this.kpiService.patchKpi(this.$scope.kpiId, kpisToPatch[0]).then(
        () => {
          this.$scope.indicators.patching = false;

          this.applyPatchToOriginalKpi(kpisToPatch[0]);
          const kpiClone = angular.copy(this.originalKpi);
          this.$rootScope.$broadcast("kpiUpdated", kpiClone);
          this.$scope.$close();
        },
        (err) => this.handleIndicatorError(err, "patching", { inline: true })
      );
    }
  };

  private selectGroups = (ids: string[]): void => {
    this.$scope.kpi.groups = ids;
  };

  private onInsightSelectorChange = () => {
    if (this.$scope.insightSelector.data.formatting) {
      const unit = this.$scope.insightSelector.data.formatting.unit;
      this.$scope.model.formatting.unit = unit ? unit : "";
      this.$scope.model.formatting.isCustom = this.$scope.insightSelector.data.formatting.isCustomUnit;
      this.$scope.kpi.formatting.suffix = this.$scope.insightSelector.data.formatting.suffix ? unit : "";
      this.$scope.kpi.formatting.prefix = this.$scope.insightSelector.data.formatting.suffix ? "" : unit;
      this.$scope.kpi.formatting.fractionSize = Number(this.$scope.insightSelector.data.formatting.decimalSize);
    }
  };

  private changeInheritsPermissions = (inheritsPermissions: boolean) => {
    if (inheritsPermissions) {
      this.$scope.kpi.access = this.defaultGranularPermissions;
      this.$scope.inheritsPermissions = true;
    } else {
      this.$scope.kpi.access.inherits = false;
      this.$scope.inheritsPermissions = false;
    }
  };

  private getOriginalKpi = (): IKpi => {
    return this.originalKpi;
  };

  private getDefaultGranularPermissions = (): IAccess => {
    return this.defaultGranularPermissions;
  };

  // removing permission shuld be detected as change in the form
  private enableSaveBtnOnUpdate = () => {
    if (this.originalAccess) {
      this.$scope.disableSaveBtn = angular.equals(this.$scope.kpi.access, this.originalAccess);
    }
  };
}
